import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, ChevronDown } from "lucide-react";
import Particles from "@/components/site/Particles";
import { useLanguage } from "@/contexts/LanguageContext";

const HERO_BG =
  "https://images.unsplash.com/photo-1776935359460-6c789e972e6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwbW9kZXJuJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzcxNjA4OTJ8MA&ixlib=rb-4.1.0&q=85";

const PHONE_HREF = "tel:+14158268922";

export default function Hero() {
  const { t } = useLanguage();

  const mailHref = `mailto:lopezappliance85@gmail.com?subject=${encodeURIComponent(
    t("quote_email.subject")
  )}&body=${encodeURIComponent(t("quote_email.body"))}`;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 kenburns bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(10,10,10,0.78)_0%,rgba(10,10,10,0.55)_45%,rgba(10,10,10,1)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,rgba(212,175,55,0.18),transparent_60%)]" />
      </div>

      <Particles count={36} />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <motion.span
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(212,175,55,0.55)] bg-black/40 backdrop-blur-md text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#d4af37]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] shadow-[0_0_10px_#ffd700]" />
            {t("hero.badge")}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            data-testid="hero-title"
            className="font-serif-display mt-6 text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
          >
            <span className="block text-white">Lopez</span>
            <span className="block gold-gradient-text italic">Appliances</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            data-testid="hero-subtitle"
            className="mt-6 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={PHONE_HREF}
              data-testid="hero-cta-call"
              className="btn-gold"
            >
              <Phone size={16} strokeWidth={2.2} />
              {t("hero.cta_call")}
            </a>
            <a
              href={mailHref}
              data-testid="hero-cta-quote"
              className="btn-outline-gold"
            >
              <Mail size={16} strokeWidth={2.2} />
              {t("hero.cta_quote")}
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-[#d4af37]/70"
        >
          <span className="text-[10px] tracking-[0.4em] uppercase mb-2">
            {t("hero.scroll")}
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={18} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
